import { FunctionComponent, useContext, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import { StreetAddressState } from '@/libs/helpers/apiClient'
import { useDebounce } from '@/libs/helpers/hooks/useDebounce'
import { OrderMethod } from '@/libs/helpers/utils'
import {
  ConfigurationActionType,
  ConfigurationContext,
  useConfiguration,
} from '@/providers/configuration/ConfigurationContext'
import { AutocompleteOption, OrderingAddress, useGoogleMaps } from '@/providers/googleMaps'
import { CheckBox, Grid, InputAutocomplete, Suggestion, Text } from '@/tbui'
import { colors } from '@/tbui/DefaultThemeProvider/defaultTheme'

const CheckboxContainer = styled(Grid)`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 12px;
`

const CheckboxText = styled(Text)`
  margin-left: 16px;
`

const StyledPrimaryText = styled.p`
  font-size: 14px;
  margin: 0;
`

const StyledSecondaryText = styled.p`
  font-size: 12px;
  margin: 0;
`

const StyledGrid = styled(Grid)`
  margin-bottom: 12px;
`

interface BillingAddressInformationProps {
  deliveryAddress?: OrderingAddress
  streetAddress: StreetAddressState | null
  setStreetAddress: (value: StreetAddressState | null) => void
  payrixZipCode: string
  setPayrixZipCode: (value: string) => void
  validBillingAddress: boolean
  setValidZipCode: (value: boolean) => void
  setValidBillingAddress: (value: boolean) => void
  orderMethod: OrderMethod
}

const BillingAddressInformation: FunctionComponent<BillingAddressInformationProps> = ({
  deliveryAddress,
  streetAddress,
  setStreetAddress,
  payrixZipCode,
  setPayrixZipCode,
  validBillingAddress,
  setValidZipCode,
  setValidBillingAddress,
  orderMethod,
}) => {
  // HOOKS
  const { t } = useTranslation()
  const [
    { placesAutocompleteOptions, orderingAddress },
    { getPlacesAutocompleteOptions, selectOrderingAddress, clearUserLocation },
  ] = useGoogleMaps()
  const { userSession } = useConfiguration()
  const { dispatch: dispatchConfiguration } = useContext(ConfigurationContext)

  // STATES
  const [isSameAddressSelected, setIsSameAddressSelected] = useState(false)

  // EFFECTS
  useEffect(() => {
    if (isSameAddressSelected && deliveryAddress) {
      setStreetAddress({
        address1: deliveryAddress.address1,
        address2: deliveryAddress.address2,
        city: deliveryAddress.city,
        state: deliveryAddress.province,
        formattedAddress: deliveryAddress.formattedAddress,
        country: deliveryAddress.country,
      })
      setPayrixZipCode(deliveryAddress.postalCode)
    }

    if (!isSameAddressSelected && orderingAddress != null) {
      if (orderingAddress.postalCode.length > 0) {
        setValidZipCode(true)
      } else {
        setValidZipCode(false)
      }

      if (orderingAddress.formattedAddress.length > 0) {
        setValidBillingAddress(true)
      }

      setStreetAddress({
        address1: orderingAddress.address1,
        address2: orderingAddress.address2,
        city: orderingAddress.city,
        state: orderingAddress.province,
        formattedAddress: orderingAddress.formattedAddress,
        country: orderingAddress.country,
      })
      setPayrixZipCode(orderingAddress.postalCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSameAddressSelected, deliveryAddress, orderingAddress])

  useEffect(() => {
    dispatchConfiguration({
      type: ConfigurationActionType.SET_USER_SESSION,
      payload: {
        ...userSession,
        billingAddress:
          streetAddress != null
            ? {
                address1: streetAddress.address1,
                address2: streetAddress.address2,
                city: streetAddress.city,
                state: streetAddress.state,
                zipcode: payrixZipCode,
              }
            : undefined,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streetAddress, payrixZipCode])

  // FUNCTIONS
  const mapAddressOptions = (options: AutocompleteOption[]): Suggestion[] => {
    return options?.map((option) => ({
      value: option.label,
      key: option.id,
      label: (
        <>
          <StyledPrimaryText>{option.primaryText}</StyledPrimaryText>
          <StyledSecondaryText>{option.secondaryText}</StyledSecondaryText>
        </>
      ),
    }))
  }

  const asyncGetPlacesAutocompleteOptions = useDebounce(
    async (inputValue: string): Promise<void> => {
      if (inputValue == null || inputValue === '') {
        clearUserLocation()
        setStreetAddress(null)
        setPayrixZipCode('')
      } else {
        await getPlacesAutocompleteOptions(inputValue)
      }
    },
    250
  )

  const handleAutocompleteSelection = (selectedSuggestion: Suggestion): void => {
    const googlePlace = placesAutocompleteOptions?.find(({ id }) => id === selectedSuggestion.key)
    if (googlePlace != null && googlePlace.id != null && googlePlace.id !== '') {
      selectOrderingAddress({ placeID: googlePlace.id })
    }
  }

  return (
    <StyledGrid container data-test="billing-address-information">
      <Text type="H2" m="0 0 16px 0">
        {t('checkout.billing_address.title')}
      </Text>

      {orderMethod === OrderMethod.delivery && (
        <CheckboxContainer>
          <CheckBox
            data-test="address-same-as-delivery"
            onChange={(e) => setIsSameAddressSelected(e.target.checked)}
            id="address-same-as-delivery"
            variant="SQUARE"
          />
          <CheckboxText color="TEXT_2">
            {t('checkout.billing_address.billing_address_same_as_delivery')}
          </CheckboxText>
        </CheckboxContainer>
      )}

      <Text type="H4" bold={false} color={isSameAddressSelected ? 'GRAY_2' : 'TEXT'}>
        {t('checkout.billing_address.street_address')}
      </Text>
      <Grid data-test="billing-address-information-street-address-container">
        <InputAutocomplete
          suggestions={mapAddressOptions(placesAutocompleteOptions ?? [])}
          onChange={asyncGetPlacesAutocompleteOptions}
          onSelect={handleAutocompleteSelection}
          data-test="billing-address-information-street-address"
          value={streetAddress?.formattedAddress}
          variant="INPUT"
          style={{ color: isSameAddressSelected ? colors.FEATHER_GREY : colors.SPACE_GREY }}
          disabled={isSameAddressSelected}
          placeholder={t('checkout.billing_address.street_address_placeholder')}
        />

        {!validBillingAddress && (
          <Text
            color="ERROR"
            m="16px 0 0 0"
            data-test="billing-address-information-street-address-error"
          >
            {streetAddress && !['US', 'CA'].includes(streetAddress?.country)
              ? t('checkout.billing_address.error_other')
              : t('checkout.billing_address.error')}
          </Text>
        )}
      </Grid>
    </StyledGrid>
  )
}

export default BillingAddressInformation
